import { useEffect, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Checkbox, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Spinner, Text, Textarea, useToast,
} from "@chakra-ui/react";
import { createImportEntry, getImportEntryPreview, ImportEntryPreview } from "../../../services/fiscalService";
import { crm } from "../../../theme";
import { brl } from "./duimp";
import { statusInfo } from "./fiscalLabels";

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

/**
 * Gerar NF de entrada a partir da DUIMP: confere emitente, despesas aduaneiras ("outras despesas"),
 * exportador, local de desembaraço e texto do fisco. Gera um rascunho (nada vai para a SEFAZ aqui).
 */
const ImportEntryModal = ({ processId, onClose, onCreated, onOpenInvoice }: {
  processId: string;
  onClose: () => void;
  onCreated: (invoiceId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
}) => {
  const toast = useToast();
  const [preview, setPreview] = useState<ImportEntryPreview | null>(null);
  const [loading, setLoading] = useState(true);
  // Troca de emitente: recarrega sem trocar o formulário por spinner
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [issuerId, setIssuerId] = useState("");
  const [expenseIds, setExpenseIds] = useState<string[]>([]);
  // Texto do fisco só vai como ajuste se a pessoa editou (senão o servidor usa o modelo do emitente escolhido)
  const [fiscoEdited, setFiscoEdited] = useState(false);
  const [form, setForm] = useState({
    exporterName: "", exporterCode: "", recipientCountry: "", recipientCountryCode: "", customsLocation: "", customsState: "", fiscoText: "",
  });

  const load = (issuer?: string) => {
    (issuer ? setReloading : setLoading)(true);
    getImportEntryPreview(processId, issuer)
      .then((p) => {
        setPreview(p);
        // Emitente só muda quando a prévia dele chegou (falha mantém o anterior, com o texto dele)
        setIssuerId(p.suggestedIssuerId ?? "");
        setFiscoEdited(false);
        // Troca de emitente: mantém o que a pessoa já marcou/digitou; o resto (texto, local) vem do emitente novo
        setExpenseIds((ids) => (issuer ? ids : p.expenses.filter((e) => e.selected).map((e) => e.id)));
        setForm((f) => ({
          exporterName: issuer ? f.exporterName : p.exporterName ?? "",
          exporterCode: issuer ? f.exporterCode : p.exporterCode ?? "",
          recipientCountry: issuer ? f.recipientCountry : p.recipientCountry ?? "",
          recipientCountryCode: issuer ? f.recipientCountryCode : p.recipientCountryCode ?? "",
          customsLocation: p.customsLocation ?? "", customsState: p.customsState ?? "", fiscoText: p.fiscoText ?? "",
        }));
      })
      .catch((e) => toast({
        status: "error",
        title: e?.response?.status === 403 ? "Sem permissão para emitir notas fiscais" : "Não foi possível carregar os dados da DUIMP",
      }))
      .finally(() => { setLoading(false); setReloading(false); });
  };
  useEffect(() => { load(); }, [processId]);

  const set = (field: keyof typeof form, value: string) => setForm((f) => ({ ...f, [field]: value }));
  const toggle = (id: string) => setExpenseIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const selectedTotal = preview?.expenses.filter((e) => expenseIds.includes(e.id)).reduce((s, e) => s + e.amount, 0) ?? 0;
  const blocked = !preview || preview.itemCount === 0 || !issuerId || reloading;

  const generate = async () => {
    setSaving(true);
    const result = await createImportEntry(processId, { issuerId, expenseIds, ...form, fiscoText: fiscoEdited ? form.fiscoText : null });
    setSaving(false);
    if (!result.success || !result.invoice) {
      toast({ status: "error", title: "Não foi possível gerar a nota", description: result.errors.join("\n"), duration: 9000, isClosable: true });
      return;
    }
    if (result.warnings.length)
      toast({ status: "warning", title: "Rascunho gerado com avisos", description: result.warnings.join("\n"), duration: 9000, isClosable: true });
    else toast({ status: "success", title: "Rascunho da NF de entrada gerado" });
    onCreated(result.invoice.id);
  };

  return (
    <Modal isOpen onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px">Gerar NF de entrada{preview?.processRef ? ` · ${preview.processRef}` : ""}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Flex justify="center" py="32px"><Spinner /></Flex>
          ) : !preview ? null : (
            <Box>
              {preview.warnings.map((w) => (
                <Alert key={w} status="warning" borderRadius="10px" mb="8px" fontSize="13px"><AlertIcon />{w}</Alert>
              ))}
              {preview.existing.length > 0 && (
                <Alert status="info" borderRadius="10px" mb="12px" fontSize="13px">
                  <AlertIcon />
                  <Box>
                    Este processo já tem NF de entrada:{" "}
                    {preview.existing.map((nf) => (
                      <Button key={nf.id} variant="link" size="sm" mr="8px" onClick={() => onOpenInvoice(nf.id)}>
                        {nf.number ? `Nº ${nf.number}` : "sem número"} ({statusInfo[nf.status]?.label ?? nf.status})
                      </Button>
                    ))}
                  </Box>
                </Alert>
              )}

              <Text fontSize="13px" color={crm.textMuted} mb="12px">
                DUIMP {preview.duimpNumber} · {preview.itemCount} itens · valores e tributos vêm da DUIMP; nada é enviado à SEFAZ nesta etapa.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                <FormControl>
                  <Label>Emitente {reloading && <Spinner size="xs" ml="6px" />}</Label>
                  <Select size="sm" value={issuerId} onChange={(e) => load(e.target.value)}>
                    {preview.issuers.map((i) => <option key={i.id!} value={i.id!}>{i.name} ({i.cnpj}){i.environment === "homologacao" ? " · homologação" : ""}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <Label>Exportador (destinatário da nota)</Label>
                  <Input size="sm" maxLength={60} value={form.exporterName} onChange={(e) => set("exporterName", e.target.value)} />
                </FormControl>
                <FormControl>
                  <Label>Código do exportador</Label>
                  <Input size="sm" maxLength={60} value={form.exporterCode} placeholder="usa o nome se vazio" onChange={(e) => set("exporterCode", e.target.value)} />
                </FormControl>
                <SimpleGrid columns={2} spacing="8px">
                  <FormControl>
                    <Label>País</Label>
                    <Input size="sm" maxLength={60} value={form.recipientCountry} onChange={(e) => set("recipientCountry", e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <Label>Código BACEN</Label>
                    <Input size="sm" maxLength={4} value={form.recipientCountryCode} onChange={(e) => set("recipientCountryCode", e.target.value)} />
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <Label>Local de desembaraço</Label>
                  <Input size="sm" list="customs-units" maxLength={60} value={form.customsLocation}
                    onChange={(e) => {
                      const known = preview.knownCustomsUnits.find((u) => u.split("|")[0] === e.target.value);
                      setForm((f) => ({ ...f, customsLocation: e.target.value, customsState: known ? known.split("|")[1] : f.customsState }));
                    }} />
                  <datalist id="customs-units">
                    {preview.knownCustomsUnits.map((u) => <option key={u} value={u.split("|")[0]} />)}
                  </datalist>
                </FormControl>
                <FormControl>
                  <Label>UF do desembaraço</Label>
                  <Input size="sm" maxLength={2} value={form.customsState} onChange={(e) => set("customsState", e.target.value.toUpperCase())} />
                </FormControl>
              </SimpleGrid>

              <Text fontSize="13px" fontWeight="700" mt="18px" mb="6px">Outras despesas (despesas aduaneiras do processo)</Text>
              {preview.expenses.length === 0 ? (
                <Text fontSize="13px" color={crm.textMuted}>Nenhum numerário de despesa no processo.</Text>
              ) : (
                <Box border={`1px solid ${crm.border}`} borderRadius="10px" px="12px" py="6px">
                  {preview.expenses.map((e) => (
                    <Flex key={e.id} justify="space-between" py="4px" fontSize="13px">
                      <Checkbox isChecked={expenseIds.includes(e.id)} onChange={() => toggle(e.id)}>
                        <Text fontSize="13px">{e.title}{e.isForecast ? " (previsão)" : ""}</Text>
                      </Checkbox>
                      <Text>{brl(e.amount)}</Text>
                    </Flex>
                  ))}
                  <Flex justify="space-between" borderTop={`1px solid ${crm.border}`} mt="4px" pt="6px" fontSize="13px" fontWeight="700">
                    <Text>Total rateado nos itens pelo CIF</Text>
                    <Text>{brl(selectedTotal)}</Text>
                  </Flex>
                </Box>
              )}

              <FormControl mt="14px">
                <Label>Informações ao fisco</Label>
                <Textarea size="sm" rows={4} maxLength={2000} value={form.fiscoText} onChange={(e) => { set("fiscoText", e.target.value); setFiscoEdited(true); }} />
              </FormControl>

              <Flex mt="14px" gap="16px" wrap="wrap" fontSize="13px" color={crm.textMuted}>
                <Text>Produtos: <b>{brl(preview.totals.products)}</b></Text>
                <Text>II: <b>{brl(preview.totals.ii)}</b></Text>
                <Text>IPI: <b>{brl(preview.totals.ipi)}</b></Text>
                <Text>PIS: <b>{brl(preview.totals.pis)}</b></Text>
                <Text>COFINS: <b>{brl(preview.totals.cofins)}</b></Text>
                <Text>Total (com as despesas marcadas ao abrir): <b>{brl(preview.totals.total)}</b></Text>
              </Flex>
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving} isDisabled={blocked} onClick={generate}>
            Gerar rascunho
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportEntryModal;
